import React from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** `wide`: مثل دیالوگ ساخت دسته‌بندی — عرض تا ~۱۲۰۰px و حاشیه/سایه یکدست */
  size?: 'small' | 'medium' | 'large' | 'xl' | 'wide';
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, size = 'medium' }) => {
  if (!isOpen) return null;

  const sizeClasses: Record<NonNullable<ModalProps['size']>, string> = {
    small: 'max-w-sm sm:max-w-md',
    medium: 'max-w-md sm:max-w-2xl',
    large: 'max-w-lg sm:max-w-4xl',
    xl: 'max-w-xl sm:max-w-6xl',
    wide: 'w-full max-w-[min(1200px,calc(100vw-24px))]',
  };

  const isWide = size === 'wide';

  const panelTone = isWide
    ? 'rounded-t-2xl sm:rounded-2xl border border-slate-200/90 shadow-[0_25px_50px_-12px_rgba(15,23,42,0.18)]'
    : 'rounded-t-2xl sm:rounded-lg shadow-xl';

  const bodyPadding = isWide ? 'px-4 pt-1 pb-4 sm:p-6 sm:pt-2 sm:pb-5' : 'px-4 pt-2 pb-4 sm:p-6 sm:pb-4';

  const headerBar = isWide
    ? 'flex items-center justify-between mb-0 sm:mb-0 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-3 pb-3 sm:pt-3 sm:pb-4 border-b border-slate-200/80 bg-gradient-to-b from-slate-50 to-white'
    : 'flex items-center justify-between mb-3 sm:mb-4';

  const titleTone = isWide
    ? 'text-base sm:text-xl leading-7 font-bold text-[#0F172A] flex-1 text-right mr-2 sm:mr-0'
    : 'text-base sm:text-lg leading-6 font-medium text-gray-900 flex-1 text-right mr-2 sm:mr-0';

  const scrollTone = isWide
    ? 'max-h-[min(72vh,calc(100dvh-180px))] sm:max-h-[min(78vh,calc(100dvh-140px))] overflow-y-auto overflow-x-hidden'
    : 'max-h-[70vh] sm:max-h-[80vh] overflow-y-auto';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end sm:items-center justify-center min-h-screen pt-4 px-2 pb-20 text-center sm:block sm:p-0 sm:px-4">
        {/* Mobile-friendly overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        {/* Mobile-first modal positioning */}
        <div
          className={`relative inline-block align-bottom bg-white text-right overflow-hidden transform transition-all w-full mx-2 sm:my-8 sm:align-middle ${sizeClasses[size]} ${isWide ? 'sm:mx-auto' : 'sm:mx-0'} ${panelTone}`}
        >
          {/* Mobile handle bar */}
          <div className="sm:hidden flex justify-center pt-3 pb-2">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
          </div>

          <div className={`bg-white ${bodyPadding}`}>
            <div className={headerBar}>
              <button
                type="button"
                onClick={onClose}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-2 hover:bg-slate-100 rounded-xl transition-colors"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h3 className={titleTone}>{title}</h3>
            </div>
            <div className={isWide ? 'pt-4 sm:pt-5' : ''}>
              <div className={scrollTone}>{children}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
